import { Box, Card, CardContent, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import CardComp from "shared/components/card";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import { getAnalyticsRetention } from "shared/services/productManagerService";
import styles from "./style.module.scss";

function AppAnalyticsRetention() {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(moment().subtract(30, "days").toDate());
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useBreadcrumbs([
    {
      label: routeConstant.dashboard.title,
      route: routeConstant.dashboard.path,
    },
    {
      label: routeConstant.appAnalytics.title,
      route: routeConstant.appAnalytics.path,
    },
    {
      label: routeConstant.appAnalyticsRetention.title,
      route: routeConstant.appAnalyticsRetention.path,
    },
  ]);

  const fetchRetentionData = () => {
    setLoading(true);
    const installDate = moment(selectedDate).format("YYYY-MM-DD");

    getAnalyticsRetention(installDate)
      .then(({ data }) => {
        setAnalyticsData(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedDate) {
      fetchRetentionData();
    }
  }, [selectedDate]);

  const retentionPeriods = [
    { key: "day_1", label: "Day 1" },
    { key: "day_7", label: "Day 7" },
    { key: "day_14", label: "Day 14" },
    { key: "day_30", label: "Day 30" },
  ];

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4">User Retention Analytics</Typography>
        <Box className={styles.datePicker}>
          <DatePicker
            selected={selectedDate}
            onChange={(date: Date) => setSelectedDate(date)}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select install date"
            className={styles.dateInput}
            maxDate={new Date()}
          />
        </Box>
      </Box>

      <Grid container spacing={3} className={styles.statsContainer}>
        <Grid item xs={12} sm={6} md={4}>
          <CardComp
            title="Install Date"
            stats={analyticsData?.install_date || "-"}
            isLoading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CardComp
            title="Total Installed"
            stats={analyticsData?.total_installed || 0}
            isLoading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} className={styles.tableContainer}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Retention Breakdown
              </Typography>
              {analyticsData?.retention && (
                <TableContainer component={Paper} className={styles.table}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Period</strong></TableCell>
                        <TableCell align="right"><strong>Active Users</strong></TableCell>
                        <TableCell align="right"><strong>Retention Rate</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {retentionPeriods.map((period) => {
                        const data = analyticsData.retention[period.key];
                        return (
                          <TableRow key={period.key}>
                            <TableCell>{period.label}</TableCell>
                            <TableCell align="right">
                              {data?.active_count || 0}
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                className={styles.retentionRate}
                                style={{
                                  color: data?.percentage >= 70 ? '#4caf50' : data?.percentage >= 50 ? '#ff9800' : '#f44336'
                                }}
                              >
                                {data?.percentage?.toFixed(2) || 0}%
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={styles.chartContainer}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Retention Visualization
              </Typography>
              {analyticsData?.retention && (
                <Box className={styles.retentionBars}>
                  {retentionPeriods.map((period) => {
                    const data = analyticsData.retention[period.key];
                    const percentage = data?.percentage || 0;
                    return (
                      <Box key={period.key} className={styles.retentionBar}>
                        <Typography variant="body2" className={styles.barLabel}>
                          {period.label}
                        </Typography>
                        <Box className={styles.barContainer}>
                          <Box
                            className={styles.barFill}
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: percentage >= 70 ? '#4caf50' : percentage >= 50 ? '#ff9800' : '#f44336'
                            }}
                          >
                            <Typography variant="caption" className={styles.barText}>
                              {percentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AppAnalyticsRetention;
